/**
 * ExpectedError — frontend mirror of the backend helper at
 * `trebbleNodeServer/helpers/ExpectedError.js`.
 *
 * Throw this (instead of a plain `Error`) from code paths whose failure is a
 * **user-facing condition** rather than an application bug:
 *   - network / connectivity failures the user can retry
 *   - validation failures the user can correct
 *   - user-content issues (corrupted uploads, unsupported formats)
 *   - navigation/abort races the user initiated
 *   - auth / session expiry prompts
 *
 * `window.sendErrorToRaygun` treats an `ExpectedError` as non-reportable to
 * Rollbar (user toast + Mixpanel tracking still fire), matching the backend's
 * behavior where `ExpectedError` instances are filtered out of Rollbar before
 * emit (see `server.js` console.error interceptor).
 *
 * Optional metadata carried on the instance:
 *   - `i18nKey` : the un-translated i18n key of the message (stable across locales)
 *   - `classification` : "network" | "validation" | "user-content" | "auth" |
 *       "navigation" | "business" | "environment" — for dashboard filtering
 *   - `expectedErrorCategory` : a stable product-specific bucket for analytics
 *   - `watchInAnalytics` : true when the issue should stay visible in Mixpanel
 *   - `statusCode` : HTTP status if applicable
 *
 * Example:
 *   throw new ExpectedError(
 *       window.getI18n(ti18n, "CONNECTION_LOST_UPLOAD"),
 *       { i18nKey: "CONNECTION_LOST_UPLOAD", classification: EXPECTED_ERROR_CLASSIFICATION.NETWORK }
 *   );
 */
/**
 * Allowed `classification` values. Keep in lockstep with
 * docs/Rollbar dashboards — widening this set is fine, narrowing
 * it breaks existing tagged throw sites.
 */
const EXPECTED_ERROR_CLASSIFICATION = Object.freeze({
    NETWORK: "network",
    VALIDATION: "validation",
    USER_CONTENT: "user-content",
    AUTH: "auth",
    NAVIGATION: "navigation",
    BUSINESS: "business",
    ENVIRONMENT: "environment",
});

const EXPECTED_ERROR_CATEGORY = Object.freeze({
    AUDIO_DECODE_OR_UNSUPPORTED_FILE: "audio_decode_or_unsupported_file",
    MEDIA_FILE_UNAVAILABLE: "media_file_unavailable",
    BROWSER_PERMISSION_DENIED: "browser_permission_denied",
    BROWSER_STORAGE_QUOTA_EXCEEDED: "browser_storage_quota_exceeded",
    HARDWARE_ACCELERATION_UNAVAILABLE: "hardware_acceleration_unavailable",
    LOGIN_ACCOUNT_NOT_FOUND: "login_account_not_found",
    PROJECT_ACCESS_DENIED: "project_access_denied",
    TRANSCRIPTION_QUOTA_EXCEEDED: "transcription_quota_exceeded",
    TRANSIENT_NETWORK_DOWNLOAD: "transient_network_download",
    TRANSIENT_NETWORK_FETCH_JSON: "transient_network_fetch_json",
    TRANSIENT_NETWORK_NO_RESPONSE: "transient_network_no_response",
    TRANSIENT_NETWORK_UPLOAD: "transient_network_upload",
    USER_ABORTED_BROWSER_OPERATION: "user_aborted_browser_operation",
});

const EXPECTED_ERROR_SOURCE = Object.freeze({
    DOWNLOAD_URI: "downloadURI",
    FETCH_JSON: "fetchJson",
    LIBRARY_PAGE_DELETE_SONG_CAPSULE: "LibraryPage.deleteSongCapsule",
    SEQUENCER_WEBGL_ERROR: "SequencerView._showWebGLErrorMessage",
    TRANSCRIBE_AND_EDIT_AUDIO: "transcribeAndEditAudio",
    UPLOAD_JSON_TO_S3_USING_PRESIGNED_URL: "uploadJSONToS3UsignPreSignedUrl",
    UPLOAD_STRING_TO_S3_USING_PRESIGNED_URL: "uploadStringToS3UsignPreSignedUrl",
    UPLOAD_TO_S3: "uploadToS3",
});

var EXPECTED_ERROR_CLASSIFICATIONS = Object.freeze({
    [EXPECTED_ERROR_CLASSIFICATION.NETWORK]: true,       // user connectivity issue (fetch failed, xhr.status === 0)
    [EXPECTED_ERROR_CLASSIFICATION.VALIDATION]: true,    // user input failed a check before server call
    [EXPECTED_ERROR_CLASSIFICATION.USER_CONTENT]: true,  // uploaded media was corrupted / unsupported
    [EXPECTED_ERROR_CLASSIFICATION.AUTH]: true,          // session expiry / unauthorised / login failed
    [EXPECTED_ERROR_CLASSIFICATION.NAVIGATION]: true,    // user-initiated abort of an in-flight request
    [EXPECTED_ERROR_CLASSIFICATION.BUSINESS]: true,      // plan/quota/state rules the user can resolve
    [EXPECTED_ERROR_CLASSIFICATION.ENVIRONMENT]: true,   // browser/device capability issues
});

function ExpectedError(message, options) {
    this.message = message || "";
    this.name = "ExpectedError";
    this.i18nKey = (options && options.i18nKey) || null;
    var requestedClassification = options && options.classification;
    this.classification = (requestedClassification && EXPECTED_ERROR_CLASSIFICATIONS[requestedClassification])
        ? requestedClassification
        : null;
    this.statusCode = (options && Object.prototype.hasOwnProperty.call(options, "statusCode")) ? options.statusCode : null;
    this.expectedErrorCategory = (options && options.expectedErrorCategory) || null;
    this.watchInAnalytics = !!(options && options.watchInAnalytics);
    this.errorCode = (options && options.errorCode) || null;
    this.source = (options && options.source) || null;
    if (typeof Error.captureStackTrace === "function") {
        Error.captureStackTrace(this, ExpectedError);
    } else {
        try {
            this.stack = new Error(message).stack;
        } catch (e) { /* stack is best-effort */ }
    }
}
ExpectedError.prototype = Object.create(Error.prototype);
ExpectedError.prototype.constructor = ExpectedError;
ExpectedError.prototype.toString = function() {
    return this.message || "";
};

// Expose globally so non-module code (legacy Backbone views/controllers) can
// use `new window.ExpectedError(...)` without importing.
if (typeof window !== "undefined") {
    window.ExpectedError = ExpectedError;
    window.EXPECTED_ERROR_CLASSIFICATION = EXPECTED_ERROR_CLASSIFICATION;
    window.EXPECTED_ERROR_CATEGORY = EXPECTED_ERROR_CATEGORY;
    window.EXPECTED_ERROR_SOURCE = EXPECTED_ERROR_SOURCE;
}

export {
    EXPECTED_ERROR_CLASSIFICATION,
    EXPECTED_ERROR_CATEGORY,
    EXPECTED_ERROR_SOURCE,
    EXPECTED_ERROR_CLASSIFICATIONS
};
export default ExpectedError;
